import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Eye, EyeOff, ArrowLeft, Building2, Mail, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../App';
import { toast } from 'sonner';

const SignupPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationInfo, setVerificationInfo] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const result = await register(name, email, password, organizationName);
      
      // Check if verification info was returned (preview mode)
      if (result.verification) {
        setVerificationInfo(result.verification);
        toast.success('Account created! Please verify your email.');
      } else {
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToDashboard = () => {
    navigate('/dashboard');
  };

  // If verification info exists (preview mode), show verification screen
  if (verificationInfo && verificationInfo.mode === 'preview') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 justify-center">
            <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-slate-900 dark:text-white">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </div>
          
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                Account Created!
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Email verification is required to unlock all features.
              </p>
            </div>
            
            {/* Preview Mode Notice */}
            <Alert className="mb-6 border-blue-200 bg-blue-50 dark:bg-blue-900/20">
              <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm">
                <strong>Preview Mode:</strong> Email verification is simulated. You can verify instantly using the button below.
              </AlertDescription>
            </Alert>
            
            {/* Verification Token Info (Preview Only) */}
            {verificationInfo.verification_token && (
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 mb-6">
                <p className="text-xs text-slate-500 mb-2">Verification Token (for testing):</p>
                <code className="text-xs bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded break-all">
                  {verificationInfo.verification_token}
                </code>
              </div>
            )}
            
            <div className="space-y-3">
              <Link to={verificationInfo.verification_url || `/verify-email?token=${verificationInfo.verification_token}`}>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="verify-email-btn">
                  <Check className="w-4 h-4" />
                  Verify Email Now
                </Button>
              </Link>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleContinueToDashboard}
                data-testid="continue-to-dashboard-btn"
              >
                Continue to Dashboard (Limited Access)
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 text-center mt-6">
              You can verify your email later from the dashboard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950 flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-slate-900 p-12">
        <div className="max-w-lg text-white">
          <h2 className="text-3xl font-bold mb-4">Start your free trial today</h2>
          <p className="text-slate-300 text-lg mb-8">
            Get full access to {process.env.REACT_APP_APP_NAME || 'Flowteras'} for 14 days. No credit card required.
          </p>
          
          <div className="space-y-4">
            {[
              'Unlimited WhatsApp conversations',
              'AI-powered reply suggestions',
              'Workflow automation',
              'Team collaboration tools',
              'Real-time analytics'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-slate-200">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-slate-900 dark:text-white">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </div>
          
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2" data-testid="signup-title">
            Create your account
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">
            Start your 14-day free trial
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="signup-name"
                className="h-12"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Work Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="signup-email"
                className="h-12"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="organization">Organization Name</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="organization"
                  type="text"
                  placeholder="Your Company"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  required
                  data-testid="signup-organization"
                  className="h-12 pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="signup-password"
                  className="h-12 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">Minimum 6 characters</p>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-emerald-700 hover:bg-emerald-800" 
              disabled={loading}
              data-testid="signup-submit"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              By signing up, you agree to our{' '}
              <Link to="/terms" className="text-emerald-700 hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-emerald-700 hover:underline">Privacy Policy</Link>
            </p>
          </form>
          
          <p className="text-center mt-8 text-slate-600 dark:text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
